import { CircularProgress, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { useEffect, useState, useCallback, useRef } from "react";
import api from "../../../../api/careiqapi";
import { useSelector } from "react-redux";
import { getPracticeId } from "../../../../config/practice";
import { mergePages, contiguousCount } from "./documentPages";
import { SandboxedHtmlFrame, ccdaEmptyMessage } from "./sandboxedHtml";

export { ccdaEmptyMessage };

// First request: the first PAGE_SIZE pages (first paint as fast as before). When the
// document has more, the rest is requested right after in the background (REST_BATCH per
// request), so every page shows without a click. All of it is free: v2 serves pages from its
// disk cache or FHIR (DiagnosticReport/Media/Binary), and caches each page once. The old
// viewer stopped at 5: v2 cached the 5-page window as the whole document and its "Load More"
// got pages 1-5 again (owner 10-07, "reports over 10 pages - I can only see 5").
const PAGE_SIZE = 5;
const REST_BATCH = 50;
const DEFAULT_ZOOM = 1.2;

// Received C-CDA rendered by the v2 gateway (api/fhir/ccda_view.py, page.render === "ccda").
// Kept as a named export for callers/tests; every HTML page now goes through the same
// sandboxed frame (see sandboxedHtml.jsx).
export const CcdaFrame = ({ data, index }) => <SandboxedHtmlFrame data={data} index={index} kind="ccda" />;

const DocumentViewerPage = ({ page, index }) => {
    // lower-cased: Athena / FHIR types arrive as sent ("TEXT/HTML" must still be sandboxed)
    const contentType = String(page.content_type || "image/png").toLowerCase();
    const isPdf = contentType.includes("pdf");
    const isHtml = contentType.includes("html");
    const isText = contentType.includes("text/plain");
    const isOctet = contentType === "application/octet-stream";

    const [blobUrl, setBlobUrl] = useState(null);
    // PDF pages rendered to images via pdf.js. Mobile Chrome cannot display
    // a PDF inside an iframe (it shows a dark "Open" download placeholder —
    // QA 2026-09-04 phone screenshot), so canvas-render like the prior-visit
    // note viewer does; the blob-URL iframe stays as a fallback when pdf.js
    // is unavailable or rendering fails.
    const [pdfImages, setPdfImages] = useState(null);

    useEffect(() => {
        if (isPdf && page.data) {
            let cancelled = false;
            const renderPdf = async () => {
                try {
                    if (window.pdfjsLib) {
                        const byteString = atob(page.data);
                        const uintArray = new Uint8Array(byteString.length);
                        for (let i = 0; i < byteString.length; i++) {
                            uintArray[i] = byteString.charCodeAt(i);
                        }
                        const pdf = await window.pdfjsLib.getDocument({ data: uintArray }).promise;
                        const images = [];
                        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                            const p = await pdf.getPage(pageNum);
                            const viewport = p.getViewport({ scale: 2 });
                            const canvas = document.createElement("canvas");
                            canvas.width = viewport.width;
                            canvas.height = viewport.height;
                            await p.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
                            images.push(canvas.toDataURL("image/png"));
                        }
                        if (!cancelled) setPdfImages(images);
                        return;
                    }
                } catch (err) {
                    console.error("pdf.js render error, falling back to iframe:", err);
                }
                // Fallback: blob URL in an iframe (desktop browsers).
                try {
                    const byteString = atob(page.data);
                    const arrayBuffer = new ArrayBuffer(byteString.length);
                    const uintArray = new Uint8Array(arrayBuffer);
                    for (let i = 0; i < byteString.length; i++) {
                        uintArray[i] = byteString.charCodeAt(i);
                    }
                    // always typed application/pdf: a blob: URL runs in the care origin, so a
                    // type merely containing "pdf" (e.g. "text/html; name=x.pdf") must never
                    // make the browser parse it as HTML (review 10-07n finding 3)
                    const blob = new Blob([uintArray], { type: "application/pdf" });
                    const url = URL.createObjectURL(blob);
                    if (!cancelled) setBlobUrl(url);
                } catch (err) {
                    console.error("Base64 render error:", err);
                }
            };
            renderPdf();
            return () => { cancelled = true; };
        }
    }, [page.data, isPdf, contentType]);

    // For plain text, decode base64 to string (React escapes it). HTML is never decoded into
    // the app DOM: it goes into SandboxedHtmlFrame (third-party content, review 10-07).
    let textContent = null;
    if ((isText || isOctet) && !isHtml && page.data) {
        try {
            textContent = atob(page.data);
        } catch (e) {
            console.error("Base64 decode error:", e);
        }
    }

    const src = isPdf
        ? (blobUrl || "")
        : (page.data ? `data:${contentType};base64,${page.data}` : page.href || "");

    return (
        <div className="flex justify-center w-full">
            {isPdf ? (
                pdfImages ? (
                    <div className="w-full flex flex-col gap-2">
                        {pdfImages.map((img, i) => (
                            <img
                                key={i}
                                src={img}
                                alt={`Document page ${i + 1}`}
                                className="w-full rounded-lg bg-white"
                            />
                        ))}
                    </div>
                ) : src ? (
                    <iframe
                        src={src}
                        title={`Document page ${page.pageid || index + 1}`}
                        className="w-full rounded-lg border-none"
                        style={{ height: "80vh" }}
                    />
                ) : (
                    <div className="w-full py-16 flex justify-center text-sm text-gray-400">
                        Rendering document...
                    </div>
                )
            ) : (isHtml && page.data) ? (
                <SandboxedHtmlFrame data={page.data} index={index} kind={page.render === "ccda" ? "ccda" : "html"} />
            ) : ((isText || isOctet) && textContent) ? (
                <div
                    className="w-full p-6 bg-gray-50 rounded-lg"
                    style={{ fontSize: '14px', lineHeight: '1.8', color: '#333', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                >
                    {textContent}
                </div>
            ) : (
                <img
                    src={src}
                    alt={`Document page ${page.pageid || index + 1}`}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                    }}
                    loading="lazy"
                    onError={() => console.error(`Failed to load page ${page.pageid}`)}
                />
            )}
        </div>
    );
};

/* Small thumbnail for the sidebar */
const PRACTICE_ID_SYNC = getPracticeId();

const PageThumbnail = ({ page, index, isActive, onClick }) => {
    const contentType = String(page.content_type || "image/png").toLowerCase();
    const isPdf = contentType.includes("pdf");
    const isHtml = contentType.includes("html");
    const isText = contentType.includes("text/plain") || contentType === "application/octet-stream";
    const isImage = !isPdf && !isHtml && !isText;

    const src = isImage && page.data
        ? `data:${contentType};base64,${page.data}`
        : null;

    return (
        <button
            onClick={onClick}
            className={`flex-shrink-0 rounded-lg border-2 overflow-hidden transition-all ${
                isActive
                    ? "border-[#5C8496] shadow-md ring-2 ring-[#5C8496]/30"
                    : "border-gray-200 hover:border-gray-400"
            }`}
            style={{ width: 80, height: 100 }}
            title={`Page ${index + 1}`}
        >
            {src ? (
                <img
                    src={src}
                    alt={`Page ${index + 1}`}
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="w-full h-full bg-gray-50 flex flex-col items-center justify-center gap-1">
                    <span className="text-gray-400 text-lg">
                        {isPdf ? "\uD83D\uDCC4" : isHtml ? "\uD83C\uDF10" : "\uD83D\uDCDD"}
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium">Page {index + 1}</span>
                </div>
            )}
        </button>
    );
};


// Clinical documents and local uploads come whole from clinical-documents-by-id (no paging).
const isWholeDocumentRequest = (procedure) => {
    const isClinical =
        procedure?.documentclass === "CLINICALDOCUMENT" ||
        (!procedure?.encounterdocumentid && !procedure?.imagingresultid && procedure?.clinicaldocumentid);
    const anyDocId =
        procedure?.clinicaldocumentid ||
        procedure?.imagingresultid ||
        procedure?.encounterdocumentid;
    const isLocalDoc = procedure?.local === true || Number(anyDocId) >= 900000000;
    return Boolean(isClinical || isLocalDoc);
};

// Pages of a later batch that the viewer does not hold yet (by page number), kept in page order.
export const appendNewPages = mergePages;

const ProcedureDocument = ({ isProcedureDocumentOpen, subview, setIsProcedureDocumentOpen, procedure }) => {
    const [pages, setPages] = useState([]);       // [{pageid, content_type, data (base64)}]
    const [totalPages, setTotalPages] = useState(0);
    // Athena subclass returned when the doc is a metadata-only stub with
    // nothing to download (patient-portal COMMUNICATORFORMs, e.g.) — lets
    // the empty state explain itself instead of a bare "No pages available".
    const [noContentSubclass, setNoContentSubclass] = useState(null);
    // Why a C-CDA shows nothing (v2 no_content_reason): flag off / not licensed / absent / unreadable.
    const [noContentReason, setNoContentReason] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    // Local-upload → Athena sync state (button only renders for local docs)
    const [syncBusy, setSyncBusy] = useState(false);
    const [syncDone, setSyncDone] = useState(false);
    const [syncMsg, setSyncMsg] = useState("");
    const [activePage, setActivePage] = useState(0);
    // Background load of the remaining pages stops after a batch that brought nothing new
    // (or failed); the "Load remaining" button then retries by hand.
    const [autoLoadStopped, setAutoLoadStopped] = useState(false);
    const pagesRef = useRef([]);
    // Bumped on every open/close: a response for an earlier opening is dropped.
    const openSeq = useRef(0);
    // AI-generated summary shown as a header above the PDF pages. Fetched
    // lazily from the dedicated /summary endpoint (cache-only, no LLM cost).
    const [aiSummary, setAiSummary] = useState(null);
    const [aiLabel, setAiLabel] = useState(null);
    const pageRefs = useRef([]);
    const patientId = procedure.patientid;
    const practiseid = getPracticeId();
    const userConfig = useSelector((state) => state.userconfig);

    const fetchDocument = useCallback(async (offset = 0, append = false) => {
        const seq = openSeq.current;
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        // Local uploads (migrated OpenEMR docs, faxes sent-to-chart) only
        // exist in the mirror's local store — procedures/image-result is
        // Athena-only and dead-ends on them. The by-id endpoint resolves
        // local ids (>= 900000000) for EVERY category (QA 2026-08-21,
        // Srividya 13195 timeline).
        const wholeDocument = isWholeDocumentRequest(procedure);
        try {
            let response;

            if (wholeDocument) {
                response = await api.post(
                    `/v1/fhir-mirror/${practiseid}/clinical-documents-by-id`,
                    {
                        patient_id: patientId,
                        // seeded users may have no config.departmentid — never
                        // drop the key (the API 422s on missing fields)
                        department_id: userConfig?.config?.departmentid ?? 1,
                        clinicaldocument_id:
                            procedure.clinicaldocumentid ||
                            procedure.imagingresultid ||
                            procedure.encounterdocumentid,
                        // list entry's Athena subclass (e.g. CLINICALDOCUMENT_CCDA): lets the
                        // gateway skip FHIR for C-CDAs and ask Athena for the XML when enabled
                        document_subclass: procedure?.documenttype || procedure?.documentsubclass || null,
                    }
                );
            } else {
                const docId = procedure.imagingresultid || procedure.encounterdocumentid;

                const category =
                    procedure.imagingresultid ? "Procedures" : "ProgressNotes";

                if (!docId) {
                    console.error("No document ID available for this document", procedure);
                    setPages([]);
                    setIsLoading(false);
                    return;
                }

                response = await api.post(
                    `/v1/fhir-mirror/${practiseid}/patient/${patientId}/procedures/image-result`,
                    {
                        patientid: patientId,
                        imagingresultid: docId,
                        fhir_resource_id: procedure.fhir_resource_id || null,
                        category,
                        page_offset: offset,
                        page_limit: append ? REST_BATCH : PAGE_SIZE,
                    }
                );
            }
            if (seq !== openSeq.current) return; // the viewer was closed or reopened meanwhile

            const docData = response?.data || response;
            const firstEntry = Array.isArray(docData) ? docData[0] : docData;
            const fetchedPages = firstEntry?.pages || [];

            if (append) {
                const next = mergePages(pagesRef.current, fetchedPages);
                // nothing new: stop the background load (the button retries by hand); a batch
                // that brought pages, a manual retry included, lets it carry on (review F5)
                setAutoLoadStopped(next === pagesRef.current);
                pagesRef.current = next;
                setPages(next);
                const total = Number(firstEntry?.total_pages) || 0;
                setTotalPages((t) => Math.max(next.length, total || t));
            } else {
                // only the first answer can be empty: why (C-CDA reason) is kept with the subclass
                setNoContentSubclass(fetchedPages.length === 0 ? (firstEntry?.no_content_subclass || null) : null);
                setNoContentReason(fetchedPages.length === 0 ? (firstEntry?.no_content_reason || null) : null);
                pagesRef.current = fetchedPages;
                setPages(fetchedPages);
                // by-id answers with the whole document: never page it (a second by-id
                // request would only repeat the same pages)
                const total = wholeDocument ? fetchedPages.length
                    : Math.max(Number(firstEntry?.total_pages) || 0, fetchedPages.length);
                setTotalPages(total);
            }
        } catch (error) {
            console.error("Error fetching document:", error.response ? error.response.data : error);
            if (seq !== openSeq.current) return;
            if (append) setAutoLoadStopped(true);
            else { pagesRef.current = []; setPages([]); }
        } finally {
            if (seq === openSeq.current) {
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        }
    }, [subview, procedure, patientId, practiseid, userConfig]);

    useEffect(() => {
        openSeq.current += 1;
        pagesRef.current = [];
        setAutoLoadStopped(false);
        setIsLoadingMore(false);
        if (
            isProcedureDocumentOpen &&
            patientId &&
            (procedure.encounterdocumentid || procedure.imagingresultid || procedure.clinicaldocumentid)
        ) {
            fetchDocument(0, false);
            // Lazy AI-summary fetch — read-only cache lookup, no LLM cost.
            // Category mirrors what /sync/0 writes into documents_cache.
            const cat = procedure.documentclass === "CLINICALDOCUMENT" || procedure.clinicaldocumentid
                ? "clinicaldocument"
                : procedure.documentclass === "ENCOUNTERDOCUMENT" || procedure.encounterdocumentid
                    ? "encounterdocument"
                    : "imagingresult";
            const docId = procedure.imagingresultid || procedure.encounterdocumentid || procedure.clinicaldocumentid;
            if (docId) {
                api.get(`/v1/${practiseid}/patients/${patientId}/documents/${cat}/${docId}/summary`)
                    .then((res) => {
                        const d = res?.data?.data || res?.data || {};
                        if (d.summary) setAiSummary(d.summary);
                        if (d.label) setAiLabel(d.label);
                    })
                    .catch((err) => {
                        // Cache miss / 404 is fine — just skip the AI summary header.
                        console.debug("[ProcedureDocument] no cached AI summary:", err?.response?.status);
                    });
            }
        }
        // Reset when closed
        if (!isProcedureDocumentOpen) {
            setPages([]);
            setTotalPages(0);
            setZoom(DEFAULT_ZOOM);
            setActivePage(0);
            setAiSummary(null);
            setAiLabel(null);
            setNoContentSubclass(null);
            setNoContentReason(null);
        }
    }, [isProcedureDocumentOpen]);

    // The rest of a longer document loads by itself once the first pages are up. Each request
    // starts after the pages held without a gap (1..k), so a page that failed in an earlier
    // batch is asked for again instead of being skipped for good (review F4).
    useEffect(() => {
        if (!isProcedureDocumentOpen || isLoading || isLoadingMore || autoLoadStopped) return;
        if (pages.length > 0 && pages.length < totalPages) {
            fetchDocument(contiguousCount(pagesRef.current), true);
        }
    }, [isProcedureDocumentOpen, isLoading, isLoadingMore, autoLoadStopped, pages.length, totalPages, fetchDocument]);

    const handleLoadMore = () => {
        if (!isLoadingMore && pages.length < totalPages) {
            fetchDocument(contiguousCount(pagesRef.current), true);
        }
    };

    const scrollToPage = (index) => {
        setActivePage(index);
        setZoom(1); // zoom to 100% on click
        const el = pageRefs.current[index];
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    if (!isProcedureDocumentOpen) return null;

    const hasMore = pages.length < totalPages;
    const showThumbnails = pages.length > 1;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50" onClick={() => setIsProcedureDocumentOpen(false)}>
            <div className="absolute inset-0 bg-black/50 opacity-50"></div>
            <div
                className="relative z-10 h-[95vh] rounded-lg bg-white shadow-custom flex flex-col overflow-hidden"
                style={{ width: '95vw', maxWidth: '1400px' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header toolbar */}
                <div className="p-3 w-full flex justify-between items-center border-b border-gray-200 flex-shrink-0">
                    <div className="font-medium text-[#1d2951] text-lg tracking-[0] leading-6 whitespace-nowrap">
                        Result {totalPages > 0 && <span className="text-sm text-gray-400 font-normal ml-2">({pages.length} of {totalPages} pages)</span>}
                    </div>
                    <div className="flex items-center gap-1">
                        {procedure?.local && (
                            <button
                                disabled={syncBusy || syncDone || procedure?.synced}
                                onClick={async () => {
                                    setSyncBusy(true);
                                    try {
                                        const r = await api.post(`/v1/fhir-mirror/${PRACTICE_ID_SYNC}/patient/${procedure.patientid}/documents/local/${procedure.clinicaldocumentid || procedure.imagingresultid || procedure.encounterdocumentid}/sync`);
                                        // 202: this file is being sent right now (another click / tab)
                                        if (r?.status === 202) {
                                            setSyncMsg(r?.message || "Already being sent to Athena");
                                        } else {
                                            setSyncDone(true);
                                            setSyncMsg((r?.message) || "Synced to Athena");
                                        }
                                    } catch (e) {
                                        setSyncMsg(e?.response?.data?.message || "Sync failed");
                                    } finally { setSyncBusy(false); }
                                }}
                                className={`mr-2 rounded-md px-3 py-1.5 text-xs font-medium text-white ${(syncDone || procedure?.synced) ? "bg-green-600" : syncBusy ? "bg-gray-300" : "bg-[#1D2951] hover:opacity-90"}`}
                                title="Push this locally uploaded document into the patient's Athena chart"
                            >
                                {(syncDone || procedure?.synced) ? "✓ Synced to Athena" : syncBusy ? "Syncing…" : "Sync to Athena"}
                            </button>
                        )}
                        {syncMsg && !syncDone && <span className="mr-2 text-xs text-red-600">{syncMsg}</span>}
                        <IconButton
                            size="small"
                            onClick={() => setZoom(z => Math.max(0.25, +(z - 0.2).toFixed(2)))}
                            title="Zoom out"
                            disabled={zoom <= 0.25}
                        >
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                        <span className="text-xs text-gray-500 min-w-[40px] text-center select-none font-medium">{Math.round(zoom * 100)}%</span>
                        <IconButton
                            size="small"
                            onClick={() => setZoom(z => Math.min(3, +(z + 0.2).toFixed(2)))}
                            title="Zoom in"
                            disabled={zoom >= 3}
                        >
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                        <div className="w-px h-5 bg-gray-200 mx-1" />
                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={() => setIsProcedureDocumentOpen(false)}
                            style={{ color: '#C7C7CB' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>

                {/* Body: thumbnail sidebar + main content */}
                <div className="flex flex-1 min-h-0 overflow-hidden">
                    {/* Thumbnail sidebar */}
                    {showThumbnails && (
                        <div
                            className="flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto p-3 flex flex-col gap-3 items-center"
                            style={{ width: 110 }}
                        >
                            {pages.map((page, i) => (
                                <div key={page.pageid || i} className="flex flex-col items-center gap-1">
                                    <PageThumbnail
                                        page={page}
                                        index={i}
                                        isActive={activePage === i}
                                        onClick={() => scrollToPage(i)}
                                    />
                                    <span className={`text-[10px] font-medium ${activePage === i ? "text-[#5C8496]" : "text-gray-400"}`}>
                                        {i + 1}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Main document area */}
                    <div className="flex-1 overflow-auto p-4">
                        {/* AI-generated summary header (cache-only fetch) —
                            shown before the document pages so providers can
                            read the gist before scrolling the PDF. Falls
                            through silently when no cached summary exists. */}
                        {aiSummary && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        AI Generated Summary
                                    </span>
                                    {aiLabel && (
                                        <span className="text-sm font-medium text-[#1d2951]">
                                            {aiLabel}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-5">
                                    {aiSummary}
                                </p>
                                <p className="text-[10px] text-gray-500 italic mt-2">
                                    AI Generated Summary may contain inaccuracies.
                                </p>
                            </div>
                        )}
                        <div
                            className="space-y-4 w-full"
                            style={{
                                transform: `scale(${zoom})`,
                                transformOrigin: 'top center',
                                transition: 'transform 0.15s ease',
                            }}
                        >
                            {isLoading ? (
                                <div className="text-center text-gray-500 h-full">
                                    <div className="mt-20">
                                        <CircularProgress />
                                    </div>
                                </div>
                            ) : pages.length > 0 ? (
                                <>
                                    {pages.map((page, index) => (
                                        <div
                                            key={page.pageid || index}
                                            ref={(el) => (pageRefs.current[index] = el)}
                                        >
                                            <DocumentViewerPage page={page} index={index} />
                                        </div>
                                    ))}
                                    {hasMore && (
                                        <div className="flex justify-center py-4">
                                            {(isLoadingMore || !autoLoadStopped) ? (
                                                <span className="flex items-center gap-2 text-sm text-gray-500" role="status">
                                                    <CircularProgress size={16} color="inherit" />
                                                    {`Loading the remaining ${totalPages - pages.length} pages…`}
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={handleLoadMore}
                                                    className="px-6 py-2.5 rounded-xl bg-[#5C8496] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                                                >
                                                    {`Load remaining ${totalPages - pages.length} pages`}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : noContentSubclass ? (
                                <div className="text-center text-gray-600 mt-16 px-8">
                                    <div className="text-4xl mb-3">📋</div>
                                    <div className="font-semibold mb-1">
                                        {noContentSubclass === "CLINICALDOCUMENT_COMMUNICATORFORM"
                                            ? "Patient-portal form"
                                            : noContentSubclass === "CLINICALDOCUMENT_CCDA"
                                                ? "External care summary (C-CDA)"
                                                : "No viewable attachment"}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {noContentSubclass === "CLINICALDOCUMENT_COMMUNICATORFORM"
                                            ? "This entry is a form the patient submitted through the portal. Athena stores it as a record only — there is no document image or file to display here. View the responses in athenaNet."
                                            : noContentSubclass === "CLINICALDOCUMENT_CCDA"
                                                ? ccdaEmptyMessage(noContentReason)
                                                : "Athena has no downloadable content for this document."}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center text-gray-500">No pages available.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProcedureDocument;
